import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Send, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

// Filter phone numbers and emails from messages
const filterContactInfo = (text: string): string => {
  return text
    .replace(/(\+?\d[\d\s\-/.]{6,}\d)/g, "[numéro masqué]")
    .replace(/[\w.+-]+@[\w.-]+\.\w{2,}/gi, "[email masqué]");
};

interface Conversation {
  listing_id: string;
  other_user_id: string;
  other_user_name: string;
  other_user_location?: string;
  listing_title: string;
  last_message: string;
  last_at: string;
  unread: number;
}

const Messages = () => {
  const { user, loading } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvo, setActiveConvo] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMsg, setNewMsg] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    loadConversations();
  }, [user]);

  useEffect(() => {
    if (!activeConvo || !user) return;
    loadMessages(activeConvo.listing_id, activeConvo.other_user_id);

    // Realtime subscription
    const channel = supabase
      .channel(`messages-${activeConvo.listing_id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "messages",
        filter: `listing_id=eq.${activeConvo.listing_id}`,
      }, (payload) => {
        setMessages((prev) => [...prev, payload.new]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [activeConvo]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadConversations = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("messages")
      .select("*, listings(title), sender:profiles!messages_sender_id_fkey(full_name, display_name, location), receiver:profiles!messages_receiver_id_fkey(full_name, display_name, location)")
      .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
      .order("created_at", { ascending: false });

    if (!data) return;

    const convoMap = new Map<string, Conversation>();
    for (const msg of data) {
      const otherId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
      const key = `${msg.listing_id}-${otherId}`;
      if (!convoMap.has(key)) {
        const otherProfile = msg.sender_id === user.id ? (msg as any).receiver : (msg as any).sender;
        const otherName = otherProfile?.display_name || otherProfile?.full_name || "Utilisateur";
        convoMap.set(key, {
          listing_id: msg.listing_id,
          other_user_id: otherId,
          other_user_name: otherName,
          other_user_location: otherProfile?.location || undefined,
          listing_title: (msg as any).listings?.title || "Annonce",
          last_message: msg.content,
          last_at: msg.created_at,
          unread: msg.receiver_id === user.id && !msg.read ? 1 : 0,
        });
      } else if (msg.receiver_id === user.id && !msg.read) {
        convoMap.get(key)!.unread++;
      }
    }
    setConversations(Array.from(convoMap.values()));
  };

  const loadMessages = async (listingId: string, otherId: string) => {
    if (!user) return;
    const { data } = await supabase
      .from("messages")
      .select("*")
      .eq("listing_id", listingId)
      .or(`and(sender_id.eq.${user.id},receiver_id.eq.${otherId}),and(sender_id.eq.${otherId},receiver_id.eq.${user.id})`)
      .order("created_at", { ascending: true });
    setMessages(data ?? []);

    // Mark as read
    await supabase
      .from("messages")
      .update({ read: true })
      .eq("listing_id", listingId)
      .eq("sender_id", otherId)
      .eq("receiver_id", user.id);
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !activeConvo || !user) return;
    const filtered = filterContactInfo(newMsg);
    const { data: inserted } = await supabase.from("messages").insert({
      listing_id: activeConvo.listing_id,
      sender_id: user.id,
      receiver_id: activeConvo.other_user_id,
      content: filtered,
    }).select("id").single();
    setNewMsg("");

    // Trigger email notification (fire and forget)
    if (inserted?.id) {
      supabase.functions.invoke("send-message-notification", {
        body: { message_id: inserted.id },
      }).catch(() => {});
    }
  };

  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container py-6 flex-1">
        <h1 className="font-display text-2xl font-bold mb-6">Messages</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[60vh]">
          {/* Conversation list */}
          <div className="bg-card rounded-lg border overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Aucune conversation</p>
              </div>
            ) : (
              conversations.map((c) => (
                <button
                  key={`${c.listing_id}-${c.other_user_id}`}
                  onClick={() => setActiveConvo(c)}
                  className={`w-full text-left p-4 border-b hover:bg-muted/50 transition-colors ${
                    activeConvo?.listing_id === c.listing_id && activeConvo?.other_user_id === c.other_user_id
                      ? "bg-primary/5"
                      : ""
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <p className="font-medium text-sm truncate">{c.other_user_name}</p>
                    {c.unread > 0 && (
                      <span className="bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {c.unread}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{c.listing_title}</p>
                  <p className="text-xs text-muted-foreground truncate mt-1">{c.last_message}</p>
                </button>
              ))
            )}
          </div>

          {/* Chat area */}
          <div className="md:col-span-2 bg-card rounded-lg border flex flex-col">
            {!activeConvo ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                Sélectionnez une conversation
              </div>
            ) : (
              <>
                <div className="p-4 border-b">
                  <p className="font-medium text-sm">{activeConvo.other_user_name}</p>
                  <p className="text-xs text-muted-foreground">{activeConvo.listing_title}</p>
                  {activeConvo.other_user_location && (
                    <p className="text-xs text-muted-foreground mt-0.5">📍 {activeConvo.other_user_location}</p>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === user.id ? "justify-end" : "justify-start"}`}
                    >
                      <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${
                        msg.sender_id === user.id
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <div className="p-3 border-t">
                  <div className="flex gap-2">
                    <Input
                      value={newMsg}
                      onChange={(e) => setNewMsg(e.target.value)}
                      placeholder="Votre message..."
                      onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                      className="flex-1"
                    />
                    <Button size="icon" onClick={sendMessage}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Messages;
